const timezoneNames = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Kyiv',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
];

export const getTimeZoneOffsetMinutes = (
  timeZone: string,
  date = new Date()
) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(date)
    .reduce(
      (acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      },
      {} as Record<string, string>
    );

  const { year, month, day, hour, minute, second } = parts;
  if (!year || !month || !day || !hour || !minute || !second) {
    return 0;
  }

  const timestamp = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );

  return Math.round((timestamp - date.getTime()) / 60000);
};

const formatOffset = (minutes: number) => {
  const sign = minutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(minutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const mins = String(absMinutes % 60).padStart(2, '0');
  return `${sign}${hours}:${mins}`;
};

export const timezoneOptions: OptionType[] = timezoneNames.map(timeZone => ({
  value: timeZone,
  label: `${timeZone} ${formatOffset(getTimeZoneOffsetMinutes(timeZone))}`,
}));

export const findMatchingTimezone = (timezone: string): string => {
  if (timezoneNames.includes(timezone)) return timezone;

  const offset = getTimeZoneOffsetMinutes(timezone);
  return (
    timezoneNames.find(tz => getTimeZoneOffsetMinutes(tz) === offset) ?? 'UTC'
  );
};

export const getDefaultTimezone = (): string =>
  findMatchingTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);

export type StoreSnapshot = {
  currentTimezone: string;
};

export const timezoneStore = {
  state: {
    currentTimezone: '',
  } as StoreSnapshot,
  listeners: new Set<() => void>(),
  subscribe: (listener: () => void) => {
    timezoneStore.listeners.add(listener);
    return () => {
      timezoneStore.listeners.delete(listener);
    };
  },
  getSnapshot: () => {
    return timezoneStore.state;
  },
  updateState: (newState: Partial<StoreSnapshot>) => {
    timezoneStore.state = { ...timezoneStore.state, ...newState };
    timezoneStore.emitChange();
  },
  emitChange: () => {
    for (const listener of timezoneStore.listeners) {
      listener();
    }
  },
};
